import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["frame", "colSpan", "iconInput", "iconGrid"]

  connect() {
    this._onModalSrcClick = this.onModalSrcClick.bind(this)
    this._onFrameLoad = this.onFrameLoad.bind(this)

    document.addEventListener("click", this._onModalSrcClick)
    this.frameTarget.addEventListener("turbo:frame-load", this._onFrameLoad)

    this.observer = new MutationObserver(this.onMutation.bind(this))
    this.observer.observe(this.frameTarget, { childList: true, subtree: false })
  }

  disconnect() {
    document.removeEventListener("click", this._onModalSrcClick)
    this.frameTarget.removeEventListener("turbo:frame-load", this._onFrameLoad)
    this.observer?.disconnect()
  }

  onModalSrcClick(event) {
    const el = event.target.closest("[data-modal-src]")
    if (!el) return
    event.preventDefault()
    this.frameTarget.src = el.dataset.modalSrc
  }

  onFrameLoad() {
    if (this.frameTarget.innerHTML.trim()) {
      this.bsModal.show()
      this.configureInitialVisibility()
      this.initModelFilterIfPresent()
      this.initDynamicFieldIfPresent()
      this.initJobStatusIfPresent()
    }
  }

  onMutation() {
    if (!this.frameTarget.innerHTML.trim()) {
      this.bsModal.hide()
    }
  }

  // Auto-updates the col_span select and shows the relevant config section when a widget type is chosen
  applyWidgetDefault(event) {
    const select = event.currentTarget
    if (this.hasColSpanTarget) {
      const defaults = JSON.parse(select.dataset.widgetDefaults || "{}")
      const col = defaults[select.value]
      if (col) this.colSpanTarget.value = col
    }
    const configTypes  = JSON.parse(select.dataset.widgetConfigTypes  || "{}")
    const categories   = JSON.parse(select.dataset.widgetCategories   || "{}")
    this.updateConfigSections(configTypes[select.value] || null, categories[select.value] || "kpi")
  }

  configureInitialVisibility() {
    const select = this.frameTarget.querySelector("[data-widget-config-types]")
    if (!select) return
    const configTypes = JSON.parse(select.dataset.widgetConfigTypes || "{}")
    const categories  = JSON.parse(select.dataset.widgetCategories  || "{}")
    this.updateConfigSections(configTypes[select.value] || null, categories[select.value] || "kpi")
  }

  filterModels() {
    const section = this.frameTarget.querySelector("[data-config-section='model']")
    if (section) this._applyModelFilter(section)
  }

  initModelFilterIfPresent() {
    const section = this.frameTarget.querySelector("[data-config-section='model']:not(.d-none)")
    if (section) this._applyModelFilter(section)
  }

  filterJobStatuses() {
    const section = this.frameTarget.querySelector("[data-config-section='job_status']")
    if (section) this._applyJobStatusFilter(section)
  }

  initJobStatusIfPresent() {
    const section = this.frameTarget.querySelector("[data-config-section='job_status']:not(.d-none)")
    if (section) this._applyJobStatusFilter(section)
  }

  _applyJobStatusFilter(section) {
    const accountSelect = section.querySelector("[data-job-statuses-by-account]")
    const statusSelect  = section.querySelector("[data-job-filter='status']")
    if (!accountSelect || !statusSelect) return

    const accountId   = accountSelect.value
    const allStatuses = JSON.parse(accountSelect.dataset.jobStatusesByAccount || "{}")

    const wantStatus = String(
      statusSelect.hasAttribute("data-initial-value")
        ? statusSelect.dataset.initialValue
        : statusSelect.value
    )
    statusSelect.removeAttribute("data-initial-value")

    const statuses = (accountId && accountId !== "all") ? (allStatuses[accountId] || []) : []
    while (statusSelect.options.length > 1) statusSelect.remove(1)
    statuses.forEach(s => {
      statusSelect.add(new Option(s.name, s.id, false, s.id === wantStatus))
    })
    statusSelect.disabled = statuses.length === 0
  }

  filterJobStatusesMulti() {
    const section = this.frameTarget.querySelector("[data-config-section='table_job_statuses']:not(.d-none)")
    if (section) this._applyJobStatusMultiFilter(section)
  }

  filterPostcodeStates() {
    const section = this.frameTarget.querySelector("[data-config-section='table_property_postcode']:not(.d-none)")
    if (!section) return
    const accountSelect = section.querySelector("[data-postcode-states-by-account]")
    const stateSelect   = section.querySelector("[data-postcode-filter='state']")
    if (!accountSelect || !stateSelect) return

    const accountId = accountSelect.value
    const allStates = JSON.parse(accountSelect.dataset.postcodeStatesByAccount || "{}")
    const states    = (accountId && accountId !== "") ? (allStates[accountId] || []) : []
    const previous  = stateSelect.value

    stateSelect.innerHTML = ""
    stateSelect.add(new Option("All states", "all"))
    states.forEach(s => stateSelect.add(new Option(s, s)))
    if (Array.from(stateSelect.options).some(o => o.value === previous)) {
      stateSelect.value = previous
    } else {
      stateSelect.value = "all"
    }
  }

  filterSchedulingGroups() {
    const section = this.frameTarget.querySelector("[data-config-section='scheduling_utilization']:not(.d-none)")
    if (!section) return
    const accountSelect = section.querySelector("[data-scheduling-groups-by-account]")
    const groupSelect   = section.querySelector("[data-scheduling-filter='group']")
    if (!accountSelect || !groupSelect) return

    const accountId = accountSelect.value
    const allGroups = JSON.parse(accountSelect.dataset.schedulingGroupsByAccount || "{}")
    const groups    = (accountId && accountId !== "") ? (allGroups[accountId] || []) : []
    const previous  = groupSelect.value

    groupSelect.innerHTML = ""
    groupSelect.add(new Option("— none (totals across all field users) —", ""))
    groups.forEach(g => groupSelect.add(new Option(g.name, g.id)))
    // Preserve previous selection when still valid.
    if (Array.from(groupSelect.options).some(o => o.value === previous)) {
      groupSelect.value = previous
    }
  }

  _applyJobStatusMultiFilter(section) {
    const accountSelect = section.querySelector("[data-job-statuses-by-account]")
    const statusSelect  = section.querySelector("[data-job-filter='status-multi']")
    if (!accountSelect || !statusSelect) return

    const accountId = accountSelect.value
    const allStatuses = JSON.parse(accountSelect.dataset.jobStatusesByAccount || "{}")
    const statuses = (accountId && accountId !== "") ? (allStatuses[accountId] || []) : []

    statusSelect.innerHTML = ""
    statuses.forEach(s => statusSelect.add(new Option(s.name, s.id)))
    statusSelect.disabled = statuses.length === 0
  }

  filterDynamicFields() {
    const section =
      this.frameTarget.querySelector("[data-config-section='dynamic_field']:not(.d-none)") ||
      this.frameTarget.querySelector("[data-config-section='table_dynamic_field']:not(.d-none)")
    if (section) this._applyDynamicFieldFilter(section)
  }

  onDynamicFieldChange() {
    const section =
      this.frameTarget.querySelector("[data-config-section='dynamic_field']:not(.d-none)") ||
      this.frameTarget.querySelector("[data-config-section='table_dynamic_field']:not(.d-none)")
    if (section) this._showDynamicValueSection(section)
  }

  initDynamicFieldIfPresent() {
    const section =
      this.frameTarget.querySelector("[data-config-section='dynamic_field']:not(.d-none)") ||
      this.frameTarget.querySelector("[data-config-section='table_dynamic_field']:not(.d-none)")
    if (!section) return
    this._applyDynamicFieldFilter(section)
  }

  _applyDynamicFieldFilter(section) {
    const classSelect = section.querySelector("[data-dynamic-filter='asset-class']")
    const fieldSelect = section.querySelector("[data-dynamic-filter='field']")
    if (!classSelect || !fieldSelect) return

    const classVal = classSelect.value
    const allFields = JSON.parse(classSelect.dataset.fieldsByClass || "{}")

    const wantField = String(
      fieldSelect.hasAttribute("data-initial-value")
        ? fieldSelect.dataset.initialValue
        : fieldSelect.value
    )
    fieldSelect.removeAttribute("data-initial-value")

    const fields = classVal ? (allFields[classVal] || []) : []
    while (fieldSelect.options.length > 1) fieldSelect.remove(1)
    fields.forEach(f => {
      fieldSelect.add(new Option(f.label, f.key, false, f.key === wantField))
    })

    this._showDynamicValueSection(section)
  }

  _showDynamicValueSection(section) {
    const classSelect = section.querySelector("[data-dynamic-filter='asset-class']")
    const fieldSelect = section.querySelector("[data-dynamic-filter='field']")
    const fieldTypeInput = section.querySelector("[data-dynamic-filter='field-type']")
    if (!fieldSelect) return

    const classVal = classSelect?.value || ""
    const fieldKey = fieldSelect.value
    let fieldType = ""

    if (classVal && fieldKey) {
      const allFields = JSON.parse(classSelect?.dataset.fieldsByClass || "{}")
      const field = (allFields[classVal] || []).find(f => f.key === fieldKey)
      fieldType = field?.type || ""

      // Rebuild select choices when field is a select type
      if (fieldType === "select") {
        const choiceSelect = section.querySelector("[data-dynamic-value-input='select']")
        if (choiceSelect && field?.choices) {
          const wantVal = String(
            choiceSelect.hasAttribute("data-initial-value")
              ? choiceSelect.dataset.initialValue
              : choiceSelect.value
          )
          choiceSelect.removeAttribute("data-initial-value")
          while (choiceSelect.options.length > 1) choiceSelect.remove(1)
          field.choices.forEach(c => {
            choiceSelect.add(new Option(c, c, false, c === wantVal))
          })
        }
      }
    }

    if (fieldTypeInput) fieldTypeInput.value = fieldType

    const activeValueType = { boolean: "boolean", select: "select", integer: "range", decimal: "range" }[fieldType]
    ;["boolean", "select", "range"].forEach(vt => {
      const el = section.querySelector(`[data-dynamic-value='${vt}']`)
      if (!el) return
      const active = vt === activeValueType
      el.classList.toggle("d-none", !active)
      el.querySelectorAll("input, select").forEach(i => { i.disabled = !active })
    })
  }

  _applyModelFilter(section) {
    const classSelect = section.querySelector("[data-model-filter='asset-class']")
    const brandSelect = section.querySelector("[data-model-filter='brand']")
    const modelSelect = section.querySelector("select[name*='asset_type_id']")
    const classVal = classSelect?.value || ""

    // — Rebuild brand options from embedded JSON (avoids option.hidden cross-browser issues) —
    if (brandSelect) {
      const allBrands = JSON.parse(brandSelect.dataset.brandOptions || "[]")
      // data-initial-value carries the server-persisted selection; consumed on first call
      const wantBrand = String(
        brandSelect.hasAttribute("data-initial-value")
          ? brandSelect.dataset.initialValue
          : brandSelect.value
      )
      brandSelect.removeAttribute("data-initial-value")

      const filtered = classVal ? allBrands.filter(b => b.class_ids.includes(classVal)) : allBrands

      while (brandSelect.options.length > 1) brandSelect.remove(1)
      filtered.forEach(b => {
        brandSelect.add(new Option(b.label, b.value, false, b.value === wantBrand))
      })
    }

    const brandVal = brandSelect?.value || ""

    // — Rebuild model options (only populated when both class + brand chosen) —
    if (modelSelect) {
      const allModels = JSON.parse(modelSelect.dataset.modelOptions || "[]")
      const wantModel = String(
        modelSelect.hasAttribute("data-initial-value")
          ? modelSelect.dataset.initialValue
          : modelSelect.value
      )
      modelSelect.removeAttribute("data-initial-value")

      const filtered = (classVal && brandVal)
        ? allModels.filter(m => m.class_id === classVal && m.brand_id === brandVal)
        : []

      while (modelSelect.options.length > 1) modelSelect.remove(1)
      filtered.forEach(m => {
        modelSelect.add(new Option(m.label, m.value, false, m.value === wantModel))
      })
    }
  }

  pickIcon(event) {
    const btn = event.currentTarget
    const icon = btn.dataset.icon

    if (this.hasIconInputTarget) this.iconInputTarget.value = icon

    if (this.hasIconGridTarget) {
      this.iconGridTarget.querySelectorAll("button[data-icon]").forEach(b => {
        const active = b.dataset.icon === icon
        b.classList.toggle("btn-primary", active)
        b.classList.toggle("btn-outline-secondary", !active)
      })
    }
  }

  updateConfigSections(activeType, category = "kpi") {
    this.frameTarget.querySelectorAll("[data-config-section]").forEach(section => {
      const isActive = section.dataset.configSection === activeType
      section.classList.toggle("d-none", !isActive)
      section.querySelectorAll("input, select, textarea").forEach(el => {
        el.disabled = !isActive
      })
    })

    // Initialise cascading model filter when that section becomes active
    if (activeType === "model") {
      const modelSection = this.frameTarget.querySelector("[data-config-section='model']")
      if (modelSection) this._applyModelFilter(modelSection)
    }

    if (activeType === "job_status") {
      const jobSection = this.frameTarget.querySelector("[data-config-section='job_status']")
      if (jobSection) this._applyJobStatusFilter(jobSection)
    }

    if (activeType === "dynamic_field") {
      const dynSection = this.frameTarget.querySelector("[data-config-section='dynamic_field']")
      if (dynSection) this._applyDynamicFieldFilter(dynSection)
    }

    if (activeType === "table_dynamic_field") {
      const dynSection = this.frameTarget.querySelector("[data-config-section='table_dynamic_field']")
      if (dynSection) this._applyDynamicFieldFilter(dynSection)
    }

    // Icon selector: irrelevant for brand logo panels and table widgets
    const iconSection = this.frameTarget.querySelector("[data-icon-section]")
    if (iconSection) {
      const hide = activeType === "brand" || category === "table"
      iconSection.classList.toggle("d-none", hide)
      iconSection.querySelectorAll("input, select, textarea").forEach(el => {
        el.disabled = hide
      })
    }
  }

  close() {
    this.frameTarget.innerHTML = ""
    this.bsModal.hide()
  }

  get bsModal() {
    return window.bootstrap.Modal.getOrCreateInstance(this.element)
  }
}
